import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Settings2, ListOrdered, RefreshCw, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Chip, Banner } from "@/components/common";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, cur } from "@/lib/api";
import { AccountPanel } from "@/components/levels/AccountPanel";
import { LevelsBoard } from "@/components/levels/LevelsBoard";
import { ActiveTrades } from "@/components/levels/ActiveTrades";
import { SettingsDrawer } from "@/components/levels/SettingsDrawer";
import { LivePanel } from "@/components/levels/LivePanel";
import { EquityCurve } from "@/components/EquityCurve";
import { DailyReport, TradeHistory } from "@/components/levels/Reports";
import { HowItWorks } from "@/components/levels/HowItWorks";
import { MasterInfo } from "@/components/levels/MasterInfo";

function FeedBanner({ s }) {
  const b = s.broker || {};
  const stale = (s.board || []).filter((r) => r.tick_age != null && r.tick_age > 180).map((r) => r.market.name);
  const pending = (s.board || []).filter((r) => !r.row || r.row.status !== "READY").length;
  return (
    <div className="space-y-2" data-testid="levels-feed-banner">
      {!b.configured && <Banner tone="warn">No MT5 account is linked, so there are no live prices and nothing trades. Index levels are shown from the published end of day table; Gold and EU Stocks 50 stay Pending until MT5 is linked in Settings.</Banner>}
      {b.configured && b.status !== "connected" && <Banner tone="error">MT5 connection: {b.status}{b.error ? `. ${b.error}` : ""}</Banner>}
      {b.rate_limited && <Banner tone="warn">MT5 request allowance exhausted. Backing off; no entries on stale prices.</Banner>}
      {b.status === "connected" && <Banner tone="ok">MT5 connected. London time {s.london_time}. {pending ? `${pending} markets still pending levels.` : "All levels published."}{stale.length ? ` Stale: ${stale.join(", ")}.` : ""}</Banner>}
    </div>
  );
}

export default function Levels() {
  const { data: s, reload } = usePoll("/levels/state", 10000);
  const [trades, setTrades] = useState([]);
  const [days, setDays] = useState([]);
  const [eq, setEq] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const logRef = useRef(null);

  const loadReports = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/levels/trades"), api.get("/levels/daily-report"), api.get("/levels/equity")]);
    setTrades(a.data); setDays(b.data); setEq(c.data);
  }, []);
  useEffect(() => {
    loadReports();
    const id = setInterval(loadReports, 30000);
    return () => clearInterval(id);
  }, [loadReports]);

  const refreshAll = () => { reload(); loadReports(); };
  const setCfg = async (body) => {
    try { await api.put("/levels/config", body); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async (path, msg, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await api.post(path); toast.success(msg); refreshAll(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!s) return <div className="text-sm text-slate-500" data-testid="levels-loading">Loading Levels</div>;
  const cfg = s.config;
  const toggleMarket = async (m, v) => {
    try { await api.put(`/levels/markets/${m.key}`, { enabled: v }); toast.success(`${m.name} ${v ? "on" : "off"}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleLive = (v) => v
    ? run("/levels/arm", "Live trading on", "Switch to LIVE? Real orders will be sent to your MT5 account for markets set to live mirror.")
    : run("/levels/disarm", "Back to paper");
  return (
    <div className="space-y-5" data-testid="levels-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-100">Levels</h1>
          <Chip status={cfg.armed ? "ARMED" : "PAPER"} label={cfg.armed ? "LIVE · ARMED" : "PAPER"} testid="levels-header-mode-badge" />
          {cfg.master_settings && <Chip status="WARNING" className="animate-none" label="Master settings" testid="levels-master-badge" />}
          {cfg.auto_size && <span className="num text-xs text-slate-400" data-testid="levels-sizing-balance">Sizing from {cur()}{Number(s.sizing_balance).toLocaleString("en-GB")}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label data-testid="levels-live-toggle-label" className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium ${cfg.armed ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-slate-800 text-slate-300"}`}>
            <span className={cfg.armed ? "text-slate-500" : "text-slate-100"}>Paper</span>
            <Switch data-testid="levels-live-toggle" checked={!!cfg.armed} onCheckedChange={toggleLive} />
            <span className={cfg.armed ? "text-amber-300" : "text-slate-500"}>Live</span>
          </label>
          <label className="flex items-center gap-2 rounded-md border border-slate-800 px-3 py-1.5 text-sm text-slate-300">
            <Switch data-testid="levels-auto-trade-toggle" checked={cfg.auto_trade} onCheckedChange={(v) => setCfg({ auto_trade: v })} />Auto-trade
          </label>
          {s.is_admin && (
            <MasterInfo balance={s.sizing_balance}>
            <label data-testid="levels-master-settings-label" className="flex cursor-help items-center gap-2 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-1.5 text-sm text-[#EBD7A0]">
              <Switch data-testid="levels-master-settings-toggle" checked={!!cfg.master_settings} onCheckedChange={async (v) => { try { await api.post("/levels/master", { enabled: v }); toast.success(v ? "Master settings on" : "Master settings off, previous values restored"); reload(); } catch (e) { toast.error(errMsg(e)); } }} />Master settings
            </label>
            </MasterInfo>
          )}
          <Button data-testid="levels-sound-toggle" variant="outline" size="sm" className="border-slate-700 bg-transparent" onClick={() => setCfg({ sound_alerts: !cfg.sound_alerts })}>
            {cfg.sound_alerts ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button data-testid="levels-settings-button" variant="outline" size="sm" className="border-slate-700 bg-transparent" onClick={() => setDrawer(true)}><Settings2 className="mr-1.5 h-4 w-4" />Settings</Button>
          <Button data-testid="levels-trade-log-button" variant="outline" size="sm" className="border-slate-700 bg-transparent" onClick={() => logRef.current?.scrollIntoView({ behavior: "smooth" })}><ListOrdered className="mr-1.5 h-4 w-4" />Trade log</Button>
          <Button data-testid="levels-refresh-button" variant="outline" size="sm" className="border-slate-700 bg-transparent" onClick={() => run("/levels/refresh", "Levels rebuilt")}><RefreshCw className="mr-1.5 h-4 w-4" />Refresh levels</Button>
          {s.is_admin && <Button data-testid="levels-reset-button" variant="outline" size="sm" className="border-rose-800 bg-transparent text-rose-300" onClick={() => run("/levels/reset", "Paper ledger reset", "Wipe all Levels trades and restore the starting balance?")}><RotateCcw className="mr-1.5 h-4 w-4" />Reset</Button>}
        </div>
      </div>
      <FeedBanner s={s} />
      <AccountPanel ledger={s.ledger} />
      <LevelsBoard board={s.board} windows={cfg.windows} day={s.trading_day} onToggle={toggleMarket} />
      <ActiveTrades trades={s.ledger.open_trades} onChange={refreshAll} />
      <LivePanel cfg={cfg} broker={s.broker} isAdmin={s.is_admin} onChange={reload} />
      <EquityCurve points={eq} />
      <DailyReport days={days} />
      <div ref={logRef}><TradeHistory trades={trades} /></div>
      <HowItWorks />
      <SettingsDrawer open={drawer} onOpenChange={setDrawer} cfg={cfg} board={s.board} isAdmin={s.is_admin} onSaved={reload} />
    </div>
  );
}
